import localforage from "localforage"
import noop from "lib/noop"
import { generate } from "library/guid/index.mjs"
import { currentUserPrefix, jsonClone } from "lib/@offline/local-draft-store"

const AUDIT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000

const store = localforage.createInstance({ name: "sfg20-audit-queue" })

function getScopedKey(key) {
    return `${currentScopePrefix()}${key}`
}

function currentScopePrefix() {
    return `${currentUserPrefix()}${currentClientId()}:`
}

function currentClientId() {
    return window._authorization?.currentClientInfo?.id ?? ""
}

export async function enqueueAudit(operation, args, dedupeKey) {
    try {
        const key = `${operation}:${dedupeKey ?? generate()}`
        const scopedKey = getScopedKey(key)
        const existing = dedupeKey ? await store.getItem(scopedKey) : null
        await store.setItem(scopedKey, {
            key,
            operation,
            args: jsonClone(args),
            seq: existing?.seq ?? Date.now(),
            updatedAt: Date.now(),
        })
        return true
    } catch (e) {
        console.warn("Failed to queue audit operation:", e)
        return false
    }
}

export async function getAllQueuedAudits() {
    try {
        const prefix = currentScopePrefix()
        const entries = []
        await store.iterate((value, key) => {
            if (!key.startsWith(prefix)) return undefined
            if (Date.now() - value.updatedAt > AUDIT_EXPIRY_MS) {
                store.removeItem(key).catch(noop)
            } else {
                entries.push(value)
            }
            return undefined
        })
        return entries.sort((a, b) => a.seq - b.seq)
    } catch (e) {
        console.warn("Failed to get queued audits:", e)
        return []
    }
}

export async function removeQueuedAudit(key) {
    try {
        await store.removeItem(getScopedKey(key))
    } catch (e) {
        console.warn("Failed to remove queued audit:", e)
    }
}

export async function updateQueuedAuditMetadata(key, updates = {}) {
    try {
        const scopedKey = getScopedKey(key)
        const entry = await store.getItem(scopedKey)
        if (!entry) return
        await store.setItem(scopedKey, { ...entry, ...updates })
    } catch (e) {
        console.warn("Failed to update queued audit metadata:", e)
    }
}

export async function clearAllQueuedAudits() {
    try {
        const prefix = currentUserPrefix()
        const keysToRemove = []
        await store.iterate((value, key) => {
            if (key.startsWith(prefix)) keysToRemove.push(key)
            return undefined
        })
        for (const key of keysToRemove) {
            await store.removeItem(key)
        }
    } catch (e) {
        console.warn("Failed to clear queued audits:", e)
    }
}
